export type SignInPhase =
  | "redirecting"
  | "microsoft"
  | "backend"
  | "profile"
  | "saml";

const PENDING_KEY = "ssa_sign_in_pending";
const PHASE_KEY = "ssa_sign_in_phase";
const STARTED_AT_KEY = "ssa_sign_in_started_at";
const PHASE_EVENT = "ssa-sign-in-phase";
const DEFAULT_STALE_MS = 15 * 60 * 1000;

function canUseSessionStorage(): boolean {
  return typeof window !== "undefined";
}

export function isSignInPending(): boolean {
  if (!canUseSessionStorage()) return false;
  return sessionStorage.getItem(PENDING_KEY) === "1";
}

export function getSignInPhase(): SignInPhase | null {
  if (!canUseSessionStorage()) return null;
  const phase = sessionStorage.getItem(PHASE_KEY);
  if (
    phase === "redirecting" ||
    phase === "microsoft" ||
    phase === "backend" ||
    phase === "profile" ||
    phase === "saml"
  ) {
    return phase;
  }
  return null;
}

export function markSignInStarted(phase: SignInPhase = "redirecting"): void {
  if (!canUseSessionStorage()) return;
  sessionStorage.setItem(PENDING_KEY, "1");
  sessionStorage.setItem(STARTED_AT_KEY, String(Date.now()));
  setSignInPhase(phase);
}

export function setSignInPhase(phase: SignInPhase): void {
  if (!canUseSessionStorage()) return;
  sessionStorage.setItem(PHASE_KEY, phase);
  window.dispatchEvent(
    new CustomEvent<SignInPhase>(PHASE_EVENT, { detail: phase }),
  );
}

export function clearSignInPending(): void {
  if (!canUseSessionStorage()) return;
  sessionStorage.removeItem(PENDING_KEY);
  sessionStorage.removeItem(PHASE_KEY);
  sessionStorage.removeItem(STARTED_AT_KEY);
  window.dispatchEvent(new CustomEvent(PHASE_EVENT, { detail: null }));
}

export function isStaleSignInPending(
  maxAgeMs: number = DEFAULT_STALE_MS,
): boolean {
  if (!isSignInPending()) return false;
  const startedAt = Number(sessionStorage.getItem(STARTED_AT_KEY));
  if (!Number.isFinite(startedAt) || startedAt <= 0) return true;
  return Date.now() - startedAt > maxAgeMs;
}

export type SignInUiState = {
  isLoading: boolean;
  phase: SignInPhase | null;
};

const idleSignInUiState: SignInUiState = { isLoading: false, phase: null };

let cachedSignInUiSnapshot: SignInUiState = idleSignInUiState;

export function getSignInUiSnapshot(): SignInUiState {
  if (!canUseSessionStorage()) return idleSignInUiState;

  const isLoading = isSignInPending();
  const phase = getSignInPhase();

  if (
    cachedSignInUiSnapshot.isLoading === isLoading &&
    cachedSignInUiSnapshot.phase === phase
  ) {
    return cachedSignInUiSnapshot;
  }

  cachedSignInUiSnapshot = { isLoading, phase };
  return cachedSignInUiSnapshot;
}

export function getSignInUiServerSnapshot(): SignInUiState {
  return idleSignInUiState;
}

/** Subscribe to sign-in UI changes; clears stale pending state on first subscribe. */
export function subscribeSignInUi(onStoreChange: () => void): () => void {
  if (!canUseSessionStorage()) return () => undefined;

  if (isStaleSignInPending()) {
    clearSignInPending();
  }

  return subscribeSignInPhase(() => onStoreChange());
}

export function subscribeSignInPhase(
  listener: (phase: SignInPhase | null) => void,
): () => void {
  if (!canUseSessionStorage()) return () => undefined;

  const handler = (event: Event) => {
    const detail = (event as CustomEvent<SignInPhase | null>).detail;
    listener(detail ?? getSignInPhase());
  };

  window.addEventListener(PHASE_EVENT, handler);
  return () => window.removeEventListener(PHASE_EVENT, handler);
}

export function getSignInLoadingLabel(
  phase: SignInPhase | null,
  authMode: "saml" | "microsoft" | "backend" | "mock",
): string {
  switch (phase) {
    case "redirecting":
      return "Redirecting to Microsoft...";
    case "microsoft":
    case "saml":
      return "Verifying Microsoft account...";
    case "backend":
      return "Connecting to server...";
    case "profile":
      return "Loading your profile...";
    default:
      return authMode === "saml" || authMode === "microsoft"
        ? "Signing in with Microsoft..."
        : "Signing in...";
  }
}
